import Dexie from 'dexie';

// TruthBound Database Schema
class TruthBoundDB extends Dexie {
  constructor() {
    super('TruthBoundDB');
    
    this.version(1).stores({
      // Bible content cache
      bibles: 'id, abbreviation, name, language',
      books: '[bibleId+id], bibleId, name, abbreviation',
      chapters: '[bibleId+id], bibleId, bookId, number',
      verses: '[bibleId+chapterId+id], bibleId, chapterId, bookId, reference',
      
      // User data
      annotations: '++id, verseId, bibleId, type, createdAt, updatedAt, *tags',
      highlights: '++id, verseId, bibleId, color, createdAt',
      notes: '++id, verseId, bibleId, createdAt, updatedAt, *tags',
      bookmarks: '++id, verseId, bibleId, createdAt, folder',
      
      // Journal entries
      journals: '++id, date, createdAt, updatedAt, *tags',
      reflections: '++id, journalId, verseId, createdAt',
      
      // Reading plans
      readingPlans: '++id, name, createdAt, startDate, isActive',
      planProgress: '++id, planId, date, completed',
      
      // Thematic compilations
      themes: '++id, name, createdAt, updatedAt, *tags',
      themeVerses: '++id, themeId, verseId, bibleId, order',
      
      // Mind maps
      mindMaps: '++id, themeId, name, createdAt, updatedAt',
      
      // User preferences
      settings: 'key',
      
      // Translation presets
      translationPresets: '++id, name, translations, createdAt',
      
      // Sync metadata
      syncLog: '++id, action, entityType, entityId, timestamp, synced',
      
      // Groups and sharing
      groups: '++id, name, inviteCode, createdAt',
      groupMembers: '++id, groupId, userId, role, joinedAt',
      sharedContent: '++id, groupId, type, contentId, sharedAt',
      
      // Voice memos
      voiceMemos: '++id, annotationId, duration, createdAt',
      
      // Sketches
      sketches: '++id, annotationId, createdAt',
      
      // Search history
      searchHistory: '++id, query, type, createdAt',
      
      // Encryption keys (encrypted with user password)
      encryptionKeys: 'id'
    });
    
    // Version 2: Add earmarks table
    this.version(2).stores({
      // Earmarks for chapter-level bookmarking
      earmarks: '++id, chapterId, bibleId, bookName, chapterNum, createdAt'
    });
  }
}

// Create database instance
const db = new TruthBoundDB();

// Initialize default settings
export async function initializeDatabase() {
  const settingsExist = await db.settings.get('initialized');
  
  if (!settingsExist) {
    await db.settings.bulkPut([
      { key: 'initialized', value: true },
      { key: 'theme', value: 'dark' },
      { key: 'fontSize', value: 'medium' },
      { key: 'fontFamily', value: 'merriweather' },
      { key: 'lineSpacing', value: 'comfortable' },
      { key: 'defaultTranslations', value: ['de4e12af7f28f599-02'] }, // KJV
      { key: 'syncEnabled', value: false },
      { key: 'lastSyncTime', value: null },
      { key: 'readingPlanReminders', value: false },
      { key: 'textToSpeechRate', value: 1.0 },
      { key: 'textToSpeechVoice', value: null },
      { key: 'gesturesEnabled', value: true },
      { key: 'animationsEnabled', value: true }
    ]);
  }
}

// Get setting value
export async function getSetting(key) {
  const setting = await db.settings.get(key);
  return setting?.value;
}

// Update setting
export async function updateSetting(key, value) {
  await db.settings.put({ key, value });
}

// Get multiple settings
export async function getSettings(keys) {
  const settings = await db.settings.bulkGet(keys);
  return settings.reduce((acc, setting) => {
    if (setting) {
      acc[setting.key] = setting.value;
    }
    return acc;
  }, {});
}

// Log sync action
export async function logSyncAction(action, entityType, entityId) {
  await db.syncLog.add({
    action,
    entityType,
    entityId,
    timestamp: new Date().toISOString(),
    synced: false
  });
}

// Get unsynced changes
export async function getUnsyncedChanges() {
  return await db.syncLog.where('synced').equals(false).toArray();
}

// Mark changes as synced
export async function markAsSynced(ids) {
  await db.syncLog.where('id').anyOf(ids).modify({ synced: true });
}

// Clear old synced logs (keep last 7 days)
export async function clearOldSyncLogs() {
  const cutoffDate = new Date();
  cutoffDate.setDate(cutoffDate.getDate() - 7);
  
  await db.syncLog
    .where('timestamp')
    .below(cutoffDate.toISOString())
    .and(log => log.synced)
    .delete();
}

// Export database for backup
export async function exportDatabase() {
  const data = {
    annotations: await db.annotations.toArray(),
    highlights: await db.highlights.toArray(),
    notes: await db.notes.toArray(),
    bookmarks: await db.bookmarks.toArray(),
    journals: await db.journals.toArray(),
    reflections: await db.reflections.toArray(),
    readingPlans: await db.readingPlans.toArray(),
    planProgress: await db.planProgress.toArray(),
    themes: await db.themes.toArray(),
    themeVerses: await db.themeVerses.toArray(),
    mindMaps: await db.mindMaps.toArray(),
    settings: await db.settings.toArray(),
    translationPresets: await db.translationPresets.toArray(),
    voiceMemos: await db.voiceMemos.toArray(),
    sketches: await db.sketches.toArray(),
    exportDate: new Date().toISOString(),
    version: '1.0.0'
  };
  
  return data;
}

// Import database from backup
export async function importDatabase(data) {
  await db.transaction('rw', 
    db.annotations, db.highlights, db.notes, db.bookmarks,
    db.journals, db.reflections, db.readingPlans, db.planProgress,
    db.themes, db.themeVerses, db.mindMaps, db.settings,
    db.translationPresets, db.voiceMemos, db.sketches,
    async () => {
      if (data.annotations) await db.annotations.bulkPut(data.annotations);
      if (data.highlights) await db.highlights.bulkPut(data.highlights);
      if (data.notes) await db.notes.bulkPut(data.notes);
      if (data.bookmarks) await db.bookmarks.bulkPut(data.bookmarks);
      if (data.journals) await db.journals.bulkPut(data.journals);
      if (data.reflections) await db.reflections.bulkPut(data.reflections);
      if (data.readingPlans) await db.readingPlans.bulkPut(data.readingPlans);
      if (data.planProgress) await db.planProgress.bulkPut(data.planProgress);
      if (data.themes) await db.themes.bulkPut(data.themes);
      if (data.themeVerses) await db.themeVerses.bulkPut(data.themeVerses);
      if (data.mindMaps) await db.mindMaps.bulkPut(data.mindMaps);
      if (data.settings) await db.settings.bulkPut(data.settings);
      if (data.translationPresets) await db.translationPresets.bulkPut(data.translationPresets);
      if (data.voiceMemos) await db.voiceMemos.bulkPut(data.voiceMemos);
      if (data.sketches) await db.sketches.bulkPut(data.sketches);
    }
  );
}

// Clear all user data
export async function clearUserData() {
  await db.transaction('rw',
    db.annotations, db.highlights, db.notes, db.bookmarks,
    db.journals, db.reflections, db.readingPlans, db.planProgress,
    db.themes, db.themeVerses, db.mindMaps, db.translationPresets,
    db.voiceMemos, db.sketches, db.searchHistory, db.syncLog,
    async () => {
      await db.annotations.clear();
      await db.highlights.clear();
      await db.notes.clear();
      await db.bookmarks.clear();
      await db.journals.clear();
      await db.reflections.clear();
      await db.readingPlans.clear();
      await db.planProgress.clear();
      await db.themes.clear();
      await db.themeVerses.clear();
      await db.mindMaps.clear();
      await db.translationPresets.clear();
      await db.voiceMemos.clear();
      await db.sketches.clear();
      await db.searchHistory.clear();
      await db.syncLog.clear();
    }
  );
}

export default db;
